import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import GenericPage from '../../components/GenericPage';
import { 
  fetchAlljoborders, 
  createAllJobOrder, 
  updateAllJobOrder, 
  deleteAllJobOrder,
  setPagination,
  setSort,
  searchAlljoborders,
  setDefaultFilter,
  changeCondition,
  addNewCondition,
  updateOptionRule,
  updateOptionValue,
  removeCondition,
  saveFilterCondition,
  setSelected,
  resetFiltersAndPagination
} from '../../store/modules/alljoborder/alljoborderActions';
import AllJobOrderForm from '../../components/pageModule/Alljoborders/AllJobOrderForm';
import { jobStatus } from '../../utils/statusUtil';

const MySwal = withReactContent(Swal);

const Alljoborders = ({ 
    excludedActions = [],
    onSelectedRowsChange = () => {}, 
    onClickedRow = () => {},
    externalCondition = {},
    selectedIds = []
}) => {

  const alljoborderState = useSelector(state => state.alljoborder);
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      dispatch(resetFiltersAndPagination());
    };
  }, []);

  const handleCreate = async (formData) => {
    try {
      const response = await dispatch(createAllJobOrder(formData));
      toast.success('AllJobOrder created successfully', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return response;
    } catch (error) {
      toast.error(error.message || 'Error creating alljoborder', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      throw error;
    }
  };

  const handleUpdate = async (id, formData) => {
    try {
      const response = await dispatch(updateAllJobOrder(id, formData));
      toast.success('AllJobOrder updated successfully', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return response;
    } catch (error) {
      toast.error(error.message || 'Error updating alljoborder', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      throw error;
    }
  };

  const handleDelete = async (id) => {
    const result = await MySwal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, cancel!',
      reverseButtons: true
    });

    if (result.isConfirmed) {
      try {
        await dispatch(deleteAllJobOrder(id));
        toast.success('AllJobOrder deleted successfully', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        return true;
      } catch (error) {
        toast.error(error.message || 'Error deleting alljoborder', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        return false;
      }
    }
    return false; 
  };

const columns = [
    {
        name: 'Job Order Number',
        selector: row => row.order_number,
        wrap: true,
        minWidth: '180px'
    },
    {
        name: 'LPO Number',
        selector: row => (row.is_lpo) ? row.lpo_number : '-',
        wrap: true,
        minWidth: '150px'
    },
    {
        name: 'Employee',
        selector: row => (row.is_lpo) ? row.employee_name : '-',
        wrap: true,
        minWidth: '150px'
    },
    {
        name: 'Job Date',
        selector: row => row.order_date,
        sortField: 'job_orders.order_date',
        sortable: true,
        wrap: true,
        minWidth: '120px'
    },
    {
        name: 'Name',
        selector: row => row?.customer_name,
        wrap: true,
        minWidth: '200px'
    },
    {
        name: 'Net Amount',
        selector: row => row.net_total,
        wrap: true,
        minWidth: '100px'
    },
    {
        name: 'Adv. Paid',
        selector: row => row.advance_payment,
        wrap: true,
        minWidth: '100px'
    },
    {
        name: 'Status',
        selector: row => jobStatus(row.status_id, row.status),
        sortField: 'job_orders.status',
        sortable: true,
        wrap: true,
        minWidth: '120px'
    },
    {
        name: 'Created At',
        selector: row => row.created_at,
        sortField: 'job_orders.created_at',
        sortable: true,
        wrap: true,
        minWidth: '150px'
    }
];


  const actions = {
    fetch: (queryFilter, activeFilter, shouldUseFilter, externalCondition) => dispatch(fetchAlljoborders(queryFilter, activeFilter, shouldUseFilter, externalCondition)),
    create: handleCreate,
    update: handleUpdate,
    delete: handleDelete,
    search: (search) => dispatch(searchAlljoborders(search)),
    setPagination: (pagination) => dispatch(setPagination(pagination)),
    setSort: (sort) => dispatch(setSort(sort)),
    setDefaultFilter: (filter) => dispatch(setDefaultFilter(filter)),
    changeCondition: (index, condition) => dispatch(changeCondition(index, condition)),
    addNewCondition: (rule) => dispatch(addNewCondition(rule)),
    updateOptionRule: (index, rule) => dispatch(updateOptionRule(index, rule)),
    updateOptionValue: (index, value) => dispatch(updateOptionValue(index, value)),
    removeCondition: (index) => dispatch(removeCondition(index)),
    saveFilterCondition: (filter) => dispatch(saveFilterCondition(filter)),
    setSelected: (selectedRows) => dispatch(setSelected(selectedRows)),
  };

  const defaultAvailableActions = ['create', 'search', 'edit'];
  const availableActions = defaultAvailableActions.filter(action => !excludedActions.includes(action));

  return (
    <>
      <GenericPage
        title="Job Orders"
        moduleName="AllJobOrder"
        moduleKey="alljoborders"
        state={alljoborderState}
        actions={actions}
        FormComponent={AllJobOrderForm}
        columns={columns}
        availableActions={availableActions}
        actionsColumnWidth="150px"
        selectableRows={false}
        onSelectedRowsChange={onSelectedRowsChange}
        onClickedRow={onClickedRow}
        selectedIds={selectedIds}
        externalCondition={externalCondition}
      />
    </>
  );
};

export default Alljoborders; 